import { Box, Button, Checkbox, Container, FormControlLabel } from '@mui/material';
import React, { FormEvent } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Observable } from 'rxjs';

import { CrisEntityPicker } from 'app/components/cris-entity-picker';
import { EntityTextField } from 'app/components/form-controls/entity-text-field';
import { EnumSelector } from 'app/components/form-controls/enum-selector';
import { RequireRole } from 'app/components/require-role';
import { Translate } from 'app/components/translate';
import { Role } from 'app/constants/role';
import {
    BusinessImpactEnum,
    BusinessImpactEnumDict,
    OverdueImpactEnum,
    OverdueImpactEnumDict,
    PriorityEnum,
} from 'app/enum';
import { Brand, Store } from 'app/model';
import { CrisRequest, makeCrisRequest } from 'app/model/cris-request';
import { dialogService } from 'app/services/dialog.service';
import { RootState } from 'app/store';
import { ModelWithoutId } from 'app/type/model-with-id';
import { InputOnChangeNormalizer } from 'app/utils/input-on-change.normalizer';

import { CrisDupeCheck } from './cris-dupe-check';
import { saveCrisRequest, selectCrisFormData, setCrisFormData, setCurrentCrisRequest } from './cris.slice';

interface CrisRequestFormProps extends RouteComponentProps {
    // Redux props
    request: ModelWithoutId<CrisRequest>;

    // Redux actions
    setCrisFormData: (request: ModelWithoutId<CrisRequest>) => void;

    saveCrisRequest: (data: ModelWithoutId<CrisRequest>, skipCris: boolean) => Observable<CrisRequest>;

    setCurrentCrisRequest: (request: CrisRequest) => void;
}

interface CrisRequestFormState {
    submitted: boolean;
    skipCris: boolean;
}

class CrisRequestForm extends React.Component<CrisRequestFormProps, CrisRequestFormState> {
    private changeHandler: InputOnChangeNormalizer;

    public constructor(props: CrisRequestFormProps | Readonly<CrisRequestFormProps>) {
        super(props);

        this.state = {
            submitted: false,
            skipCris: false,
        };

        this.changeHandler = new InputOnChangeNormalizer((name, value) => {
            if ('entity' === name) {
                let elementName: string | undefined;

                if ('string' === typeof value) {
                    // free form entry
                    elementName = value;
                    value = undefined;
                }

                if (!value) {
                    // user cleared value which is ok here since input element is optional
                    this.props.setCrisFormData({
                        ...this.props.request,
                        brand: undefined,
                        brandData: undefined,
                        elementName,
                        storeData: [],
                    });

                    return;
                }

                const { first, second } = value as { first?: Brand; second?: Store };
                this.props.setCrisFormData({
                    ...this.props.request,
                    brand: first,
                    brandData: first
                        ? {
                              name: first.name,
                              category: first.category,
                              midCount: first.midCount,
                              locationId: first.locationId,
                          }
                        : undefined,
                    storeData: second ? [second] : [],
                    elementName: undefined,
                });

                return;
            }

            this.props.setCrisFormData({
                ...this.props.request,
                [name]: value,
            });
        });
    }

    public componentWillUnmount(): void {
        // clear brand/store selection when we leave for reduced headache with duplicate checker
        this.props.setCrisFormData({
            ...this.props.request,
            brand: undefined,
            brandData: undefined,
            elementName: '',
            storeData: [],
        });
    }

    public componentDidMount(): void {
        const query = new URLSearchParams(this.props.location.search);
        if ('seam' === query.get('from')) {
            this.setState({
                ...this.state,
                skipCris: true,
            });
            this.props.setCrisFormData({
                ...this.props.request,
                customer: 'Affinity',
                project: 'Affinity',
                businessImpact: BusinessImpactEnum.OTHER,
                overdueImpact: OverdueImpactEnum.OTHER,
            });
        }
    }

    public render(): React.ReactNode {
        const value = this.props.request.elementName
            ? this.props.request.elementName
            : {
                  first: this.props.request.brand,
                  second: this.props.request.storeData[0] as Store,
              };

        const entityValid = 'string' === typeof value || value.first !== undefined;

        return (
            <Container maxWidth={false}>
                <CrisDupeCheck name={this.props.request.elementName} brand={this.props.request.brand} />
                <form onSubmit={(evt) => this.handleSubmit(evt)}>
                    <h2>
                        Create MidMatch Request&nbsp;
                        <RequireRole roles={Role.SEAM_REQUESTS}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={this.state.skipCris}
                                        onChange={(evt, skipCris) => this.setState({ ...this.state, skipCris })}
                                    />
                                }
                                label='Skip Taxonomy Stage'
                            />
                        </RequireRole>
                    </h2>
                    <Box display='flex' flexDirection='row' sx={{ gap: '1em', flexWrap: 'wrap' }}>
                        <CrisEntityPicker
                            name='entity'
                            onChange={this.changeHandler.onChange}
                            value={value}
                            required
                            freeSolo={!this.state.skipCris}
                            sx={{ width: '20em' }}
                            variant='standard'
                            error={this.state.submitted && !entityValid}
                            helperText={this.getRequiredErrorText(
                                entityValid,
                                'Start typing to see suggestions from existing stores',
                            )}
                        />
                        <EntityTextField
                            label={<Translate tkey='forms:Customer' />}
                            helperText={this.getRequiredErrorText(
                                this.props.request.customer,
                                'If internal enter Affinity',
                            )}
                            name={'customer'}
                            onChange={this.changeHandler.onChange}
                            value={this.props.request}
                            required
                            error={this.state.submitted && !this.props.request.customer}
                        />
                        <EntityTextField
                            label={<Translate tkey='forms:Project' />}
                            helperText={this.getRequiredErrorText(
                                this.props.request.project,
                                'If internal enter Affinity',
                            )}
                            name='project'
                            onChange={this.changeHandler.onChange}
                            value={this.props.request}
                            required
                            error={this.state.submitted && !this.props.request.project}
                        />
                        <EnumSelector
                            label={<Translate tkey='forms:Priority' />}
                            name='priority'
                            onChange={this.changeHandler.onChange}
                            value={this.props.request}
                            sx={{ width: '14em' }}
                            theEnum={PriorityEnum}
                            labelDictionary={{
                                [PriorityEnum.P1]: 'P1 - signed contract',
                                [PriorityEnum.P2]: 'P2 - required for product',
                                [PriorityEnum.P3]: 'P3 - contract in review',
                                [PriorityEnum.P4]: 'P4 - Presale, nice to have',
                            }}
                        />
                        <EnumSelector
                            label={<Translate tkey='forms:Business Impact' />}
                            name='businessImpact'
                            onChange={this.changeHandler.onChange}
                            value={this.props.request}
                            required
                            sx={{ width: '12em' }}
                            theEnum={BusinessImpactEnum}
                            labelDictionary={BusinessImpactEnumDict}
                            error={this.state.submitted && !this.props.request.businessImpact}
                            helperText={this.getRequiredErrorText(this.props.request.businessImpact)}
                        />
                        <EntityTextField
                            label={<Translate tkey='forms:Requested Due Date' />}
                            type='date'
                            className='mb-1'
                            name='dueDate'
                            onChange={this.changeHandler.onChange}
                            value={this.props.request}
                            required
                            InputLabelProps={{
                                shrink: true,
                            }}
                            error={this.state.submitted && !this.props.request.dueDate}
                            helperText={this.getRequiredErrorText(this.props.request.dueDate)}
                        />
                        <EnumSelector
                            label={<Translate tkey='forms:Overdue Impact' />}
                            name='overdueImpact'
                            onChange={this.changeHandler.onChange}
                            value={this.props.request}
                            sx={{ width: '12em' }}
                            theEnum={OverdueImpactEnum}
                            required
                            labelDictionary={OverdueImpactEnumDict}
                            error={this.state.submitted && !this.props.request.overdueImpact}
                            helperText={this.getRequiredErrorText(this.props.request.overdueImpact)}
                        />
                        <EntityTextField
                            label={<Translate tkey='forms:JIRA Ticket URL' />}
                            helperText='If you have a jira link you may link it here'
                            sx={{ width: '20em' }}
                            name='jiraLink'
                            onChange={this.changeHandler.onChange}
                            value={this.props.request}
                        />
                    </Box>
                    <EntityTextField
                        label={<Translate tkey='forms:Extra Information' />}
                        multiline
                        rows={5}
                        sx={{ width: '100%' }}
                        className='mt-2'
                        name='description'
                        onChange={this.changeHandler.onChange}
                        value={this.props.request}
                        helperText='Provide any additional information that you think
                        would be useful to handling this request(for example company URL).'
                    />
                    <Box display='flex' flexDirection='row' my={3}>
                        <Button variant='contained' color='primary' type='submit'>
                            Create
                        </Button>
                    </Box>
                </form>
            </Container>
        );
    }

    private getRequiredErrorText(value: unknown, defaultText?: string) {
        if (!this.state.submitted) {
            return defaultText;
        }
        if (value) {
            return defaultText;
        }

        return 'This value is required';
    }

    private handleSubmit(evt: FormEvent): boolean {
        evt.preventDefault();

        this.setState({
            ...this.state,
            submitted: true,
        });

        const data = { ...this.props.request };
        if (data.store) {
            data.storeData = [data.store];
        }

        // this truly sucks, there has to be a better way to do validation than having to implement it yourself...
        // oh right this is React... nevermind... no standard way of doing anything
        if (
            !(data.brand || data.elementName) ||
            !data.customer ||
            !data.project ||
            !data.businessImpact ||
            !data.dueDate ||
            !data.overdueImpact
        ) {
            // required data is missing.
            return false;
        }

        if (this.state.skipCris && 0 === data.storeData.length) {
            dialogService.showError('You must select a valid store when choosing to Skip Taxonomy Stage');

            return false;
        }

        this.props.saveCrisRequest(data, this.state.skipCris).subscribe((value) => {
            this.props.setCrisFormData(makeCrisRequest()); // clear the data on successful save
            this.props.setCurrentCrisRequest(value);
            this.props.history.push(`/cris/request/${value.id}/summary`);
        });

        return false;
    }
}

const mapStateToProps = (state: RootState) => ({
    request: selectCrisFormData(state),
});

const connected = connect(mapStateToProps, { setCrisFormData, saveCrisRequest, setCurrentCrisRequest })(
    withRouter(CrisRequestForm),
);

export { connected as CrisRequestForm };
